export interface FrameStats {
  /** New pictures this source has produced since it was created. */
  frames: number;
  /** performance.now() when the latest new picture was observed; 0 if none yet. */
  lastFrameAt: number;
}

/** Implemented (duck-typed, not part of the SourceRuntime contract) by every video-backed runtime. */
export interface FrameStatsProvider {
  getFrameStats(): FrameStats;
  /**
   * True while new pictures should keep arriving at about the source's frame
   * rate, so a gap of seconds means the picture froze: a live camera or guest
   * track, a playing video. False while a gap is legitimate: canvas sources
   * that redraw only on change, a still image, a paused clip, a screen share
   * of a static window (browsers deliver capture frames only on change).
   */
  expectsContinuousFrames?(): boolean;
}

export function hasFrameStats(runtime: unknown): runtime is FrameStatsProvider {
  return typeof (runtime as Partial<FrameStatsProvider> | null)?.getFrameStats === 'function';
}

/** Kinds whose runtimes predate `expectsContinuousFrames` and always carry a live camera-like track. */
const CONTINUOUS_KINDS: ReadonlySet<string> = new Set(['camera', 'remote-guest']);

/**
 * Whether a frozen-picture alert makes sense for this runtime right now. The
 * runtime's own answer wins; without one only live track kinds qualify, so a
 * source that legitimately idles never raises a false alarm.
 */
export function expectsContinuousFrames(runtime: FrameStatsProvider, kind: string): boolean {
  if (typeof runtime.expectsContinuousFrames === 'function') return runtime.expectsContinuousFrames();
  return CONTINUOUS_KINDS.has(kind);
}

export class FrameCounter {
  private frames = 0;
  private lastFrameAt = 0;

  note(count = 1, at: number = performance.now()): void {
    if (count <= 0) return;
    this.frames += count;
    this.lastFrameAt = at;
  }

  stats(): FrameStats {
    return { frames: this.frames, lastFrameAt: this.lastFrameAt };
  }
}

type FrameCallbackElement = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: () => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
  webkitDecodedFrameCount?: number;
};

/**
 * Counts new pictures shown by a <video>. requestVideoFrameCallback is exact while the page is
 * presented but goes silent for hidden pages, so `poll()` also reads the element's own decoded
 * frame counter (and, failing that, `currentTime`). When the callback fired since the last poll
 * the poll only moves its baseline, so no frame is counted twice.
 */
export class ElementFrameObserver {
  private handle: number | null = null;
  private running = false;
  private callbackCount = 0;
  private callbackCountAtPoll = 0;
  private lastDecoded = -1;
  private lastTime = -1;

  constructor(
    private readonly el: HTMLVideoElement,
    private readonly counter: FrameCounter,
    private readonly now: () => number = () => performance.now(),
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.arm();
  }

  stop(): void {
    this.running = false;
    const el = this.el as FrameCallbackElement;
    if (this.handle !== null) el.cancelVideoFrameCallback?.(this.handle);
    this.handle = null;
  }

  poll(): void {
    const decoded = this.decodedFrames();
    const callbackActive = this.callbackCount !== this.callbackCountAtPoll;
    this.callbackCountAtPoll = this.callbackCount;
    if (decoded !== null) {
      if (this.lastDecoded >= 0 && decoded > this.lastDecoded && !callbackActive) {
        this.counter.note(decoded - this.lastDecoded, this.now());
      }
      this.lastDecoded = decoded;
      return;
    }
    const t = this.el.currentTime;
    if (t !== this.lastTime) {
      if (this.lastTime >= 0 && !callbackActive) this.counter.note(1, this.now());
      this.lastTime = t;
    }
  }

  private arm(): void {
    const el = this.el as FrameCallbackElement;
    if (!this.running || typeof el.requestVideoFrameCallback !== 'function') return;
    this.handle = el.requestVideoFrameCallback(() => {
      this.callbackCount++;
      this.counter.note(1, this.now());
      this.arm();
    });
  }

  private decodedFrames(): number | null {
    const el = this.el as FrameCallbackElement;
    if (typeof el.getVideoPlaybackQuality === 'function') {
      const total = el.getVideoPlaybackQuality().totalVideoFrames;
      if (typeof total === 'number') return total;
    }
    return typeof el.webkitDecodedFrameCount === 'number' ? el.webkitDecodedFrameCount : null;
  }
}
